import React, { useState, useEffect } from 'react';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import SearchIcon from '@mui/icons-material/Search';
import ConfirmationPopup from '../../pure-components/ConfirmationPopup';
import { ErrorToast, SuccessToast } from '../../pure-components/CustomToast';
import { toast } from 'react-toastify';
import API from '../../services/api';

const columns = [
  { key: 'name', label: 'Subscriber', width: '180px' },
  { key: 'email', label: 'Email', width: '220px' },
  { key: 'type', label: 'Type', width: '100px' },
  { key: 'period', label: 'Period', width: '150px', sortKey: 'createdAt' },
  { key: 'projects', label: 'Projects', width: '100px' },
  { key: 'licenses', label: 'Licenses', width: '100px' },
  { key: 'downloads', label: 'Downloads', width: '100px' },
  { key: 'status', label: 'Status', width: '120px' },
];

const typeOptions = ['Pro', 'Free', 'Solo'];

const SubscriberWorld = () => {
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [popupObj, setPopupObj] = useState({});
  const [data, setData] = useState([]);
  const [sortCol, setSortCol] = useState('createdAt'); // Default to createdAt
  const [sortDir, setSortDir] = useState('desc'); // Default to descending (latest first)
  const [search, setSearch] = useState('');
  const [type, setType] = useState([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [pendingRow, setPendingRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEntries, setTotalEntries] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);

  const itemsPerPage = 30;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showTypeDropdown && !event.target.closest('.type-dropdown')) {
        setShowTypeDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showTypeDropdown]);

  // Reset currentPage when filters or sort change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, type, dateFrom, dateTo, sortCol, sortDir]);

  // Fetch data function
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const filters = {
        search,
        type,
        dateFrom,
        dateTo
      };

      const result = await API.getSubscribersData(
        currentPage, 
        itemsPerPage, 
        sortCol, 
        sortDir, 
        filters
      );

      const mappedData = result.data.map(user => {
        let userStatus = 'inactive';
        if (user.status === true || user.status === 'active' || user.status === 'Active') {
          userStatus = 'active';
        } else if (user.status === false || user.status === 'inactive' || user.status === 'Inactive') {
          userStatus = 'inactive';
        }

        // console.log(`User ${user.email} - API status: ${user.status}, Mapped status: ${userStatus}`);

        return {
          id: user._id || user.id,
          name: user.last_name === '-' ? user.first_name : `${user.first_name} ${user.last_name}`.trim(),
          email: user.email,
          type: user.userType === 'solo_pro' ? 'Pro' : 
                user.userType.charAt(0).toUpperCase() + user.userType.slice(1).toLowerCase(),
          period: `${new Date(user.createdAt).toISOString().slice(0, 10)} to ${new Date(user.updatedAt).toISOString().slice(0, 10)}`,
          projects: user.projectCount || 0,
          licenses: user.licenseCount || 0,
          downloads: user.downloadedTracksIndex?.length || 0,
          status: userStatus,
        };
      });

      setData(mappedData);
      setTotalPages(result.pagination?.totalPages || 1);
      setTotalEntries(result.pagination?.total || 0);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to fetch data. Please try again.');
      setData([]);
      setTotalPages(1);
      setTotalEntries(0);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on mount and when dependencies change
  useEffect(() => {
    fetchData();
  }, [currentPage, search, type, dateFrom, dateTo, sortCol, sortDir]);

  // Handle status change
  const handleStatusChange = async (rowIdx, newStatus) => {
    setPendingRow(rowIdx);
    setPendingStatus(newStatus);
    setPopupObj({
      title: 'Confirm Status Change',
      msg: `Are you sure you want to change status to ${newStatus === 'active' ? 'Active' : 'Inactive'}?`
    });
    setShowConfirmPopup(true);
  };

  // Confirm status change with API call
  const confirmStatusChange = async () => {
    const rowIdx = pendingRow;
    const newStatus = pendingStatus;
    const user = data[rowIdx];
    const oldStatus = user.status;
    setShowConfirmPopup(false);
    setPendingRow(null);
    setPendingStatus(null);
    try {
      // Optimistic update
      setData(prev => prev.map((row, idx) => 
        idx === rowIdx ? { ...row, status: newStatus } : row
      ));
      const apiStatus = newStatus === 'active' ? true : false;
      // console.log(`Updating status for user ${user.email}: ${oldStatus} → ${newStatus} (API payload: { _id: ${user.id || user._id}, status: ${apiStatus} })`);
      const response = await API.updateSubscribersData({ 
        _id: user.id || user._id, 
        status: apiStatus 
      });
      // console.log('API response:', response);
      toast(({ closeToast }) => <SuccessToast message={response?.message || "Status updated successfully!"} />);
      // Refresh data instead of reloading page
      await fetchData();
    } catch (error) {
      // Rollback on error
      setData(prev => prev.map((row, idx) => 
        idx === rowIdx ? { ...row, status: oldStatus } : row
      ));
      let errorMsg = "Failed to update status. Please try again.";
      if (error?.message) errorMsg = error.message;
      if (error?.response?.data?.message) errorMsg = error.response.data.message;
      console.error('API error:', error);
      toast(({ closeToast }) => <ErrorToast message={errorMsg} />);
    }
  };

  const cancelStatusChange = () => {
    setShowConfirmPopup(false);
    setPendingRow(null);
    setPendingStatus(null);
  };

  const handleTypeChange = (selectedType) => {
    if (selectedType === 'All Types') {
      setType([]);
    } else {
      setType(prev => {
        if (prev.includes(selectedType)) {
          return prev.filter(t => t !== selectedType);
        } else {
          return [...prev, selectedType];
        }
      });
    }
  };

  const handleSort = (col, sortKey) => {
    const effectiveSortKey = sortKey || col;
    if (sortCol === effectiveSortKey) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(effectiveSortKey);
      setSortDir('desc'); // Default to descending for new column
    }
  };

  const clearFilters = () => {
    setSearch('');
    setType([]);
    setDateFrom('');
    setDateTo('');
    setCurrentPage(1);
  };

  const truncateText = (text, maxLength) => {
    if (!text || text === '-') return text;
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const getTypeDisplayText = () => {
    if (type.length === 0) {
      return 'All Types';
    } else if (type.length === 1) {
      return type[0];
    } else {
      return `${type.length} Types Selected`;
    }
  };

  return (
    <div className="p-4 max-w-full">
      {/* Custom Confirmation Popup */}
      {showConfirmPopup && (
        <ConfirmationPopup
          yesAction={confirmStatusChange}
          cancelAction={cancelStatusChange}
          title={popupObj.title}
          msg={popupObj.msg}
        />
      )}
      {/* Search and Filters */}
      <div className="flex flex-col lg:flex-row lg:items-end gap-4 mb-6 w-full">
        {/* Search Bar */}
        <div className="relative flex-1 min-w-[200px] max-w-full lg:max-w-md">
          <span className="absolute left-3 top-1/2 transform -translate-y-1/2">
            <SearchIcon fontSize="small" className="text-gray-400" />
          </span>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search subscribers by name..."
            className="pl-10 pr-3 h-12 border border-gray-200 rounded-md text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-200"
          />
        </div>
        
        {/* Date Filters */}
        <div className="flex flex-col sm:flex-row gap-4 w-full lg:w-auto">
          <input
            type="date"
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
            className="h-12 border border-gray-200 rounded-md text-sm bg-white text-gray-700 w-full sm:w-44 min-w-[120px] px-3 focus:outline-none"
            placeholder="From Date"
          />
          <input
            type="date"
            value={dateTo}
            onChange={e => setDateTo(e.target.value)}
            className="h-12 border border-gray-200 rounded-md text-sm bg-white text-gray-700 w-full sm:w-44 min-w-[120px] px-3 focus:outline-none"
            placeholder="To Date"
          />
        </div>
        
        {/* Type Dropdown and Clear Button */}
        <div className="flex flex-col sm:flex-row gap-4 w-full lg:w-auto">
          <div className="relative type-dropdown w-full sm:w-44">
            <button
              onClick={() => setShowTypeDropdown(!showTypeDropdown)}
              className="h-12 border border-gray-200 rounded-md text-sm bg-white text-gray-700 w-full px-4 focus:outline-none flex items-center justify-around"
            >
              <span className="truncate">{getTypeDisplayText()}</span>
              <svg 
                className={`w-4 h-4 flex-shrink-0 transition-transform ${showTypeDropdown ? 'rotate-180' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {showTypeDropdown && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-10">
                <div className="py-1">
                  <label className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={type.length === 0}
                      onChange={() => handleTypeChange('All Types')}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">All Types</span>
                  </label>
                  
                  {typeOptions.map(typeOption => (
                    <label key={typeOption} className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={type.includes(typeOption)}
                        onChange={() => handleTypeChange(typeOption)}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700">{typeOption}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <button
            onClick={clearFilters}
            className="h-12 px-4 bg-gray-100 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-200 transition-colors whitespace-nowrap w-full sm:w-auto"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="py-12 text-center text-gray-500">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
            Loading...
          </div>
        ) : error ? (
          <div className="py-12 text-center text-red-500">
            {error}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#f7f8fa] border-b border-gray-200">
                    {columns.map(col => (
                      <th
                        key={col.key}
                        className="text-gray-700 font-semibold px-4 py-3 text-left whitespace-nowrap cursor-pointer"
                        style={{ minWidth: col.width }}
                        onClick={() => handleSort(col.key, col.sortKey)}
                      >
                        <div className="flex items-center">
                          {col.label}
                          {sortCol === (col.sortKey || col.key) && (
                            <svg
                              className={`w-4 h-4 ml-1 ${sortDir === 'asc' ? 'rotate-180' : ''}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.length === 0 ? (
                    <tr>
                      <td colSpan={columns.length} className="text-center py-12 text-gray-500">
                        No subscribers found. Try adjusting your filters.
                      </td>
                    </tr>
                  ) : (
                    data.map((row, idx) => (
                      <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3" style={{ minWidth: columns[0].width }}>
                          <div
                            className="font-medium text-gray-900 overflow-auto max-h-16"
                            title={row.name}
                            style={{
                              wordBreak: 'break-word',
                              overflowWrap: 'break-word',
                              scrollbarWidth: 'thin',
                              scrollbarColor: '#cbd5e0 #f7fafc',
                            }}
                          >
                            {truncateText(row.name, 30)}
                          </div>
                        </td>
                        <td className="px-4 py-3" style={{ minWidth: columns[1].width }}>
                          <div
                            className="text-gray-900 overflow-auto max-h-16"
                            title={row.email}
                            style={{
                              wordBreak: 'break-word',
                              overflowWrap: 'break-word',
                              scrollbarWidth: 'thin',
                              scrollbarColor: '#cbd5e0 #f7fafc',
                            }}
                          >
                            {truncateText(row.email, 30)}
                          </div>
                        </td>
                        <td className="px-4 py-3" style={{ minWidth: columns[2].width }}>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            row.type === 'Pro' ? 'bg-purple-100 text-purple-800' :
                            row.type === 'Solo' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {row.type}
                          </span>
                        </td>
                        <td className="px-4 py-3" style={{ minWidth: columns[3].width }}>
                          <div className="text-gray-900 truncate text-xs">{row.period}</div>
                        </td>
                        <td className="px-4 py-3 text-center" style={{ minWidth: columns[4].width }}>
                          <div className="text-gray-900 font-medium">{row.projects}</div>
                        </td>
                        <td className="px-4 py-3 text-center" style={{ minWidth: columns[5].width }}>
                          <div className="text-gray-900 font-medium">{row.licenses}</div>
                        </td>
                        <td className="px-4 py-3 text-center" style={{ minWidth: columns[6].width }}>
                          <div className="text-gray-900 font-medium">{row.downloads}</div>
                        </td>
                        <td className="px-4 py-3" style={{ minWidth: columns[7].width }}>
                          <select
                            className={`w-full px-2 py-1 rounded text-xs font-medium border-0 focus:outline-none focus:ring-2 focus:ring-offset-1 transition-all cursor-pointer ${
                              row.status === 'active'
                                ? 'bg-green-100 text-green-800 focus:ring-green-200'
                                : 'bg-red-100 text-red-800 focus:ring-red-200'
                            }`}
                            value={row.status}
                            onChange={e => handleStatusChange(idx, e.target.value)}
                          >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </select>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50 gap-4">
                <div className="text-sm text-gray-700">
                  Showing {Math.min((currentPage - 1) * itemsPerPage + 1, totalEntries)} to {Math.min(currentPage * itemsPerPage, totalEntries)} of {totalEntries} entries (Total Pages: {totalPages})
                </div>
                <div className="flex items-center gap-2">
                  <button
                    className="px-3 py-1 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                  <div className="flex gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      return (
                        <button
                          key={pageNum}
                          className={`px-3 py-1 rounded-md border transition-colors text-sm ${
                            currentPage === pageNum
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                          }`}
                          onClick={() => setCurrentPage(pageNum)}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    className="px-3 py-1 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SubscriberWorld;